import * as _ from 'lodash';
import * as moment from 'moment';
import { ICenterBase, ICenterUnosDataMonthly, ICenterUnosDataMonthlyWithStatus } from '@nkr/models/index';
import { GridWrapper } from '@nkr/views/microsite/campaigns/styles';
import { CellValueChangedEvent, ColDef, FirstDataRenderedEvent, GridColumnsChangedEvent, AgGridEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowNode, RowSelectedEvent, ValueGetterParams, ValueParserParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FormEvent, useMemo, useState } from 'react';
import { Button, FormControl, Glyphicon } from 'react-bootstrap';
import SortableHeaderComponent from '../center/list/common/sortable-header';
import { Utility } from '@nkr/services/utility';

interface IProps {
    data: ICenterUnosDataMonthlyWithStatus[],
    centers: ICenterBase[],
    loading: boolean,
    gridHeight?: string,
    isCenter: boolean,
    onGridReady: (e: GridReadyEvent) => void,
    onRowSelected?: (e: RowSelectedEvent) => void,
    onDataChanged: (id: number, property: string, value: string) => void,
    onSavePendingChanges: () => void,
    onAddData: () => void,
    onDeleteData: () => void
}

const MonthlyData = (props: IProps) => {
    const [currentCenter, setCurrentCenter] = useState(0);
    const [currentYear, setCurrentYear] = useState(0);
    const [selectedRowsCount, setSelectedRowsCount] = useState(0);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();

        props.onGridReady(e);
    };

    const onSortChanged = (e: AgGridEvent) => {
        e.api.refreshCells();       
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridColumnsChanged = (e: GridColumnsChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const onCellValueChanged = (e: CellValueChangedEvent) => {
        let data = e.data as ICenterUnosDataMonthly;
        props.onDataChanged(data.id, e.column.getColId(), e.newValue);
    };

    const onRowSelected = (e: RowSelectedEvent) => {
        if (props.onRowSelected) {
            props.onRowSelected(e);
        }

        let rows = e.api.getSelectedNodes().filter(x => x.isSelected());
        setSelectedRowsCount(rows.length);
    };

    const getGridHeight = () => {
        if (props.gridHeight != null) {
            return props.gridHeight;
        }

        return props.data.length > 0 ? (props.data.length * 30) < 400 ? ((props.data.length * 30) + 31).toString + 'px' : '391px' : '400px';
    };

    const handleCenterChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        setCurrentCenter(parseInt(e.currentTarget.value));
    };

    const handleYearChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        setCurrentYear(parseInt(e.currentTarget.value));
    };

    const filteredData = props.data.filter(x => {
        let centerFilter = currentCenter > 0 ? x.cId == currentCenter : true;
        let yearFilter = currentYear > 0 ? moment(x.date, 'MM-YYYY').year() == currentYear : true;

        return centerFilter && yearFilter;
    });

    const cols: ColDef[] = [
        {
            colId: 'rowNumber',
            headerName: 'R#',
            width: 45,
            sortable: false,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
        },
        {
            colId: 'centerAlias',
            field: 'cAlias',
            headerName: 'Center',
            width: 125,
            hide: props.isCenter == true,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
        },
        {
            colId: 'dataDate',
            field: 'dataDate',
            headerName: 'Month',
            width: 80,
            editable: true,
            sortable: true,
            valueParser: (params: ValueParserParams) => moment(params.newValue, 'MM-YYYY').isValid() ? params.newValue : params.oldValue,
            cellRendererFramework: (params: ICellRendererParams) => <>{moment(params.value).format('MM-YYYY')}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Month</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            colId: 'ddTxps',
            field: 'ddTxps',
            headerName: 'DD Transplants',
            width: 70,
            editable: true,
            valueParser: (params: ValueParserParams) => isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue),
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>DD<br />TXPs</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ICenterUnosDataMonthlyWithStatus;
                
                return Number(data.ddTxps);
            },
        },
        {
            colId: 'ldTxps',
            field: 'ldTxps',
            headerName: 'LD Transplants',
            width: 70,
            editable: true,
            valueParser: (params: ValueParserParams) => isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue),
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>LD<br />TXPs</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ICenterUnosDataMonthlyWithStatus;
                
                return Number(data.ldTxps);
            },
        },
        {
            colId: 'wlTotal',
            field: 'wlTotal',
            headerName: 'Waitlist Total',
            width: 70,
            editable: true,
            valueParser: (params: ValueParserParams) => isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue),
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>WL<br />Total</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ICenterUnosDataMonthlyWithStatus;
                
                return Number(data.wlTotal);
            },
        },
        {
            colId: 'wlActive',
            field: 'wlActive',
            headerName: 'Waitlist Active',
            width: 70,
            editable: true,
            valueParser: (params: ValueParserParams) => isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue),
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>WL<br />Active</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ICenterUnosDataMonthlyWithStatus;
                
                return Number(data.wlActive);
            },
        },

        {
            colId: 'lda',
            field: 'lda',
            headerName: 'LD ABO A',
            width: 70,
            editable: true,
            valueParser: (params: ValueParserParams) => isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue),
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>LD<br />ABO A</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ICenterUnosDataMonthlyWithStatus;
                
                return Number(data.lda);
            },
        },
        {
            colId: 'ldb',
            field: 'ldb',
            headerName: 'LD ABO B',
            width: 70,
            editable: true,
            valueParser: (params: ValueParserParams) => isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue),
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>LD<br />ABO B</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ICenterUnosDataMonthlyWithStatus;
                
                return Number(data.ldb);
            },
        },
        {
            colId: 'ldab',
            field: 'ldab',
            headerName: 'LD ABO AB',
            width: 70,
            editable: true,
            valueParser: (params: ValueParserParams) => isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue),
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>LD<br />ABO AB</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ICenterUnosDataMonthlyWithStatus;
                
                return Number(data.ldab);
            },
        },
        {
            colId: 'ldo',
            field: 'ldo',
            headerName: 'LD ABO O',
            width: 70,
            editable: true,
            valueParser: (params: ValueParserParams) => isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue),
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>LD<br />ABO O</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ICenterUnosDataMonthlyWithStatus;
                
                return Number(data.ldo);
            },
        },

        {
            colId: 'ldra',
            field: 'ldra',
            headerName: 'LD R ABO A',
            width: 70,
            editable: true,
            valueParser: (params: ValueParserParams) => isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue),
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>LD R<br />ABO A</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ICenterUnosDataMonthlyWithStatus;
                
                return Number(data.ldra);
            },
        },
        {
            colId: 'ldrb',
            field: 'ldrb',
            headerName: 'LD R ABO B',
            width: 70,
            editable: true,
            valueParser: (params: ValueParserParams) => isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue),
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>LD R<br />ABO B</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ICenterUnosDataMonthlyWithStatus;
                
                return Number(data.ldrb);
            },
        },
        {
            colId: 'ldrab',
            field: 'ldrab',
            headerName: 'LD R ABO AB',
            width: 70,
            editable: true,
            valueParser: (params: ValueParserParams) => isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue),
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>LD R<br />ABO AB</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ICenterUnosDataMonthlyWithStatus;
                
                return Number(data.ldrab);
            },
        },
        {
            colId: 'ldro',
            field: 'ldro',
            headerName: 'LD R ABO O',
            width: 70,
            editable: true,
            valueParser: (params: ValueParserParams) => isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue),
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>LD R<br />ABO O</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ICenterUnosDataMonthlyWithStatus;
                
                return Number(data.ldro);
            },
        },

        {
            colId: 'status',
            field: 'status',
            headerName: 'Updates?',
            width: 90,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value == '1' ? <Glyphicon glyph='ok' style={{ color: 'lightseagreen' }} /> : null}</>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Updates<br />Pending?</SortableHeaderComponent>,
        },
        {
            colId: 'selected',
            // field: 'selected',
            headerName: '',
            width: 25,
            headerCheckboxSelection: false,
            checkboxSelection: true
        },
    ]

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => filteredData, [props.data, filteredData]);

    const showSave = rowData.filter(x => x.status > 0).length > 0;

    const years = _.orderBy(_.uniq(props.data.map(x => moment(x.date, 'MM-YYYY').get('year'))), year => year, 'desc');

    const gridHeight = getGridHeight();

    return (
        <>
            {props.isCenter == false && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                        <div style={{ marginRight: 5 }}>Filter by Center:</div>
                        <div style={{ marginRight: 20 }}>
                            <FormControl
                                componentClass='select'
                                value={currentCenter}
                                onChange={handleCenterChange}
                            >
                                <option value={0}>- All Centers -</option>
                                {props.centers.map(center =>
                                    <option key={'ctr-' + center.id} value={center.id}>{center.alias}</option>
                                )}
                            </FormControl>
                        </div>

                        <div style={{ marginRight: 5 }}>Filter by Year:</div>
                        <div>
                            <FormControl
                                componentClass='select'
                                value={currentYear}
                                onChange={handleYearChange}
                            >
                                <option value={0}>- All Years -</option>
                                {years.map(year =>
                                    <option key={'yr-' + year} value={year}>{year}</option>
                                )}
                            </FormControl>
                        </div>
                    </div>

                    <div>
                        <Button onClick={props.onAddData}>Add Data for Center</Button>

                        {showSave && (
                            <Button bsStyle='success' style={{ marginLeft: 10 }} onClick={props.onSavePendingChanges}>Save Pending Changes</Button>
                        )}

                        {selectedRowsCount > 0 && (
                            <Button bsStyle='danger' style={{ marginLeft: 10 }} onClick={props.onDeleteData}>Delete Selected Rows ({selectedRowsCount})</Button>
                        )}
                    </div>
                </div>
            )}

            <GridWrapper className='ag-theme-bootstrap' style={{ height: gridHeight }}>
                <AgGridReact
                    defaultColDef={{
                        headerClass: 'optn-srtr-header',
                        cellClass: 'data-cell',
                        sortable: true,
                        suppressMenu: true
                    }}
                    columnDefs={columnDefs}
                    rowData={rowData}
                    headerHeight={44}
                    rowHeight={30}
                    suppressContextMenu={true}
                    suppressRowClickSelection={true}
                    rowSelection={'multiple'}
                    immutableData={true}
                    onGridReady={onGridReady}
                    onSortChanged={onSortChanged}
                    onFirstDataRendered={onFirstDataRendered}
                    onGridColumnsChanged={onGridColumnsChanged}
                    onCellValueChanged={onCellValueChanged}
                    onRowSelected={onRowSelected}
                    getRowNodeId={(data: ICenterUnosDataMonthly) => data.id.toString()}
                />
            </GridWrapper>

            <div>{rowData.length} rows...</div>
        </>
    );
}

export default MonthlyData;